import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { runHandledApiCall } from "../../../app/apiErrorHandling";
import { useAppDispatch } from "../../../app/hooks";
import {
  createTesSalaryTableSeries,
  editTesSalaryTableSeries,
  fetchTesSalaryTablesGroupedBySeries,
  TesSalaryTableSeriesCommandPayload,
  TesSalaryTableDocument,
  TesSalaryTableCommandResult,
  TesSalaryTablesGroupedBySeriesResult,
} from "../../tesSalaryTables/tesSalaryTablesApi";
import {
  fetchTesSalaryTableConfigThunk,
  fetchTesSalaryTablesByTesThunk,
} from "../../tesSalaryTables/tesSalaryTablesSlice";

export type FilterMode = "all" | "published" | "drafts" | "recent";

export const LEGACY_SERIES_ID = "__legacy_no_series__";
const PROJECTION_RETRY_ATTEMPTS = 12;
const PROJECTION_RETRY_DELAY_MS = 500;

const delay = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const mergeGroupedWithLegacyTables = (
  tesId: string,
  grouped: TesSalaryTablesGroupedBySeriesResult[],
  flat: TesSalaryTableDocument[],
  legacySeriesName: string,
  legacySeriesDescription: string,
) => {
  const groupedIds = new Set(
    grouped.flatMap((group) => group.tables.map((table) => table.tesSalaryTableId)),
  );
  const legacyTables = flat.filter(
    (table) => !table.seriesId || !groupedIds.has(table.tesSalaryTableId),
  );
  if (!legacyTables.length) return grouped;

  return [
    ...grouped,
    {
      series: {
        tesSalaryTableSeriesId: LEGACY_SERIES_ID,
        version: 0,
        tesId,
        name: legacySeriesName,
        description: legacySeriesDescription,
      },
      tables: legacyTables,
    },
  ];
};

const extractSeriesFromCreateSeriesCommandResponse = (
  response: TesSalaryTableCommandResult<TesSalaryTableSeriesCommandPayload>,
  tesId: string,
): TesSalaryTablesGroupedBySeriesResult["series"] | null => {
  const seriesCreatedEvent = response.find(
    (event) =>
      event.name === "tesSalaryTableSeriesCreated" && event.payload.tesId === tesId,
  );
  if (!seriesCreatedEvent) return null;

  const payload = seriesCreatedEvent.payload;
  return {
    tesSalaryTableSeriesId: seriesCreatedEvent.id,
    version: seriesCreatedEvent.version,
    tesId: payload.tesId,
    name: payload.name,
    description:
      typeof payload.description === "string" ? payload.description : null,
  };
};

export const hasDuplicateSeriesNameAndEffectiveDate = ({
  groupedTables,
  seriesId,
  name,
  effectiveDate,
  excludedTableId,
}: {
  groupedTables: TesSalaryTablesGroupedBySeriesResult[];
  seriesId?: string | null;
  name: string;
  effectiveDate: string;
  excludedTableId?: string;
}) => {
  if (!seriesId) return false;
  const normalizedName = name.trim().toLowerCase();
  if (!normalizedName || !effectiveDate) return false;

  const series = groupedTables.find(
    (group) => group.series.tesSalaryTableSeriesId === seriesId,
  );
  if (!series) return false;

  return series.tables.some(
    (table) =>
      table.tesSalaryTableId !== excludedTableId &&
      table.effectiveDate === effectiveDate &&
      table.name.trim().toLowerCase() === normalizedName,
  );
};

export const useTesSalaryTablesAdminData = (tesId: string | null) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [groupedTables, setGroupedTables] = useState<
    TesSalaryTablesGroupedBySeriesResult[]
  >([]);
  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const [isSeriesDialogOpen, setIsSeriesDialogOpen] = useState(false);
  const [newSeriesName, setNewSeriesName] = useState("");
  const [newSeriesDescription, setNewSeriesDescription] = useState("");
  const [isCreatingSeries, setIsCreatingSeries] = useState(false);
  const [seriesToEdit, setSeriesToEdit] = useState<
    TesSalaryTablesGroupedBySeriesResult["series"] | null
  >(null);
  const [editedSeriesName, setEditedSeriesName] = useState("");
  const [editedSeriesDescription, setEditedSeriesDescription] = useState("");
  const [isSavingSeriesEdit, setIsSavingSeriesEdit] = useState(false);

  const loadGroupedTables = useCallback(
    (currentTesId: string) => {
      return Promise.all([
        runHandledApiCall({
          dispatch,
          run: () => fetchTesSalaryTablesGroupedBySeries(currentTesId),
        }),
        dispatch(fetchTesSalaryTablesByTesThunk(currentTesId)).unwrap(),
      ]).then(([grouped, flatResult]) =>
        mergeGroupedWithLegacyTables(
          currentTesId,
          grouped,
          flatResult.tables,
          t("settings:tesSalaryTablesLegacySeriesName"),
          t("settings:tesSalaryTablesLegacySeriesDescription"),
        ),
      );
    },
    [dispatch, t],
  );

  const reload = useCallback(() => {
    if (!tesId) return Promise.resolve<TesSalaryTablesGroupedBySeriesResult[]>([]);

    dispatch(fetchTesSalaryTableConfigThunk(tesId));
    return loadGroupedTables(tesId)
      .then((nextGroups) => {
        setGroupedTables(nextGroups);
        return nextGroups;
      })
      .catch(() => {
        setGroupedTables([]);
        return [];
      });
  }, [dispatch, loadGroupedTables, tesId]);

  const waitForSeriesProjection = useCallback(
    async (currentTesId: string, seriesId: string) => {
      for (let attempt = 0; attempt < PROJECTION_RETRY_ATTEMPTS; attempt += 1) {
        try {
          const nextGroups = await loadGroupedTables(currentTesId);
          setGroupedTables(nextGroups);
          if (
            nextGroups.some(
              (group) => group.series.tesSalaryTableSeriesId === seriesId,
            )
          ) {
            return true;
          }
        } catch {
          // Keep retrying while projections catch up.
        }

        if (attempt < PROJECTION_RETRY_ATTEMPTS - 1) {
          await delay(PROJECTION_RETRY_DELAY_MS);
        }
      }
      return false;
    },
    [loadGroupedTables],
  );

  useEffect(() => {
    reload();
  }, [reload]);

  const filteredGroups = useMemo(() => {
    const recentThreshold = Date.now() - 1000 * 60 * 60 * 24 * 14;

    const matchesFilter = (table: TesSalaryTableDocument) => {
      if (filterMode === "all") return true;
      if (filterMode === "published") return table.status === "PUBLISHED";
      if (filterMode === "drafts") {
        return table.status === "DRAFT" || table.status === "PUBLISHING";
      }
      return (table.updatedAt || 0) >= recentThreshold;
    };

    return groupedTables
      .map((group) => ({
        ...group,
        tables: [...group.tables]
          .filter(matchesFilter)
          .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)),
      }))
      .filter((group) => group.tables.length > 0 || filterMode === "all")
      .sort((a, b) => {
        const aUpdated = a.tables[0]?.updatedAt || a.series.updatedAt || 0;
        const bUpdated = b.tables[0]?.updatedAt || b.series.updatedAt || 0;
        return bUpdated - aUpdated;
      });
  }, [filterMode, groupedTables]);

  const openSeriesEditDialog = (
    series: TesSalaryTablesGroupedBySeriesResult["series"],
  ) => {
    setSeriesToEdit(series);
    setEditedSeriesName(series.name);
    setEditedSeriesDescription(series.description || "");
  };

  const closeSeriesEditDialog = () => {
    setSeriesToEdit(null);
    setEditedSeriesName("");
    setEditedSeriesDescription("");
    setIsSavingSeriesEdit(false);
  };

  const closeCreateSeriesDialog = () => {
    setIsSeriesDialogOpen(false);
    setNewSeriesName("");
    setNewSeriesDescription("");
  };

  const submitSeriesEdit = async () => {
    if (!seriesToEdit || !editedSeriesName.trim() || isSavingSeriesEdit) {
      return;
    }
    setIsSavingSeriesEdit(true);
    try {
      await runHandledApiCall({
        dispatch,
        run: () =>
          editTesSalaryTableSeries(
            {
              tesSalaryTableSeriesId: seriesToEdit.tesSalaryTableSeriesId,
              version: seriesToEdit.version,
              name: editedSeriesName.trim(),
              description: editedSeriesDescription.trim() || null,
            },
          ),
      });
      closeSeriesEditDialog();
      await reload();
    } finally {
      setIsSavingSeriesEdit(false);
    }
  };

  const submitCreateSeries = async () => {
    if (!tesId || !newSeriesName.trim() || isCreatingSeries) return;

    setIsCreatingSeries(true);
    const fallbackSeries: TesSalaryTablesGroupedBySeriesResult["series"] = {
      tesSalaryTableSeriesId: crypto.randomUUID(),
      version: 0,
      tesId,
      name: newSeriesName.trim(),
      description: newSeriesDescription.trim() || null,
    };

    try {
      const response = await runHandledApiCall({
        dispatch,
        run: () => createTesSalaryTableSeries(fallbackSeries),
      });
      const createdSeries =
        extractSeriesFromCreateSeriesCommandResponse(response, tesId) || fallbackSeries;

      setGroupedTables((prev) => {
        const withoutCreated = prev.filter(
          (group) =>
            group.series.tesSalaryTableSeriesId !==
            createdSeries.tesSalaryTableSeriesId,
        );
        return [
          {
            series: {
              ...createdSeries,
              updatedAt: createdSeries.updatedAt || Date.now(),
            },
            tables: [],
          },
          ...withoutCreated,
        ];
      });

      closeCreateSeriesDialog();

      const projectionCaughtUp = await waitForSeriesProjection(
        tesId,
        createdSeries.tesSalaryTableSeriesId,
      );
      if (!projectionCaughtUp) {
        await reload();
      }
    } finally {
      setIsCreatingSeries(false);
    }
  };

  return {
    groupedTables,
    filteredGroups,
    filterMode,
    setFilterMode,
    reload,
    isSeriesDialogOpen,
    setIsSeriesDialogOpen,
    newSeriesName,
    setNewSeriesName,
    newSeriesDescription,
    setNewSeriesDescription,
    isCreatingSeries,
    closeCreateSeriesDialog,
    submitCreateSeries,
    seriesToEdit,
    editedSeriesName,
    setEditedSeriesName,
    editedSeriesDescription,
    setEditedSeriesDescription,
    isSavingSeriesEdit,
    openSeriesEditDialog,
    closeSeriesEditDialog,
    submitSeriesEdit,
  };
};
